import React, { useEffect, useState, useCallback } from 'react';
import { Plus, X, Copy, RefreshCw, CheckCircle2, Clock, AlertTriangle, Calendar } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { formatBRL } from '../utils/FormatDecimal';
import {
  Periodic,
  PeriodicCadence,
  PeriodicOccurrence,
  listPeriodics,
  createPeriodic,
  updatePeriodic,
  deletePeriodic,
  getPeriodicStatus,
  getCalendarToken,
  regenerateCalendarToken,
  getDeclaredAccounts,
} from '../services/apiService';

const CADENCE_LABELS: Record<PeriodicCadence, string> = {
  monthly: 'Mensal',
  yearly: 'Anual',
  every_n_months: 'A cada N meses',
};

function StatusBadge({ status }: { status: string }) {
  if (status === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="h-3 w-3" /> Pago
      </span>
    );
  }
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
        <AlertTriangle className="h-3 w-3" /> Vencido
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">
        <Clock className="h-3 w-3" /> Próximo
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
      <Calendar className="h-3 w-3" /> Futuro
    </span>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

const emptyForm = (): Periodic => ({
  id: '',
  description: '',
  account: '',
  payment_account: '',
  expected_amount: null,
  cadence: 'monthly',
  day_of_month: 10,
  month: null,
  n_months: null,
  start: new Date().toISOString().slice(0, 10),
  end: null,
  alert_days_before: 3,
  active: true,
});

export default function PeriodicasPage() {
  const { canWrite } = useAuth();
  const [periodics, setPeriodics] = useState<Periodic[]>([]);
  const [occurrences, setOccurrences] = useState<PeriodicOccurrence[]>([]);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Periodic>(emptyForm());
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [calendarToken, setCalendarToken] = useState<string | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const today = new Date();
      const from = new Date(today);
      from.setMonth(from.getMonth() - 1);
      const to = new Date(today);
      to.setMonth(to.getMonth() + 3);

      const [pRes, sRes, aRes, tRes] = await Promise.all([
        listPeriodics(),
        getPeriodicStatus({
          from: from.toISOString().slice(0, 10),
          to: to.toISOString().slice(0, 10),
          include_inactive: false,
        }),
        getDeclaredAccounts(),
        getCalendarToken(),
      ]);
      setPeriodics(pRes.periodics);
      setOccurrences(sRes.occurrences);
      setAccounts(aRes.accounts);
      setCalendarToken(tRes.token);
    } catch (err) {
      console.error('Failed to load periodics', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (p: Periodic) => {
    setEditing(p.id);
    setForm({ ...p });
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);
    try {
      const payload: Periodic = {
        ...form,
        expected_amount: form.expected_amount || null,
        end: form.end || null,
      };
      if (editing) {
        await updatePeriodic(editing, payload);
      } else {
        await createPeriodic(payload);
      }
      setShowForm(false);
      setLoading(true);
      await loadData();
    } catch (err: any) {
      setFormError(err.response?.data?.detail || err.message || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Remover periódica '${id}'?`)) return;
    try {
      await deletePeriodic(id);
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Erro ao remover');
    }
  };

  const handleToggleActive = async (p: Periodic) => {
    try {
      await updatePeriodic(p.id, { ...p, active: !p.active });
      await loadData();
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Erro ao atualizar');
    }
  };

  const handleRegenerateToken = async () => {
    if (calendarToken && !window.confirm('Regenerar token vai invalidar o atual. Continuar?')) return;
    try {
      const res = await regenerateCalendarToken();
      setCalendarToken(res.token);
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Erro ao gerar token');
    }
  };

  const handleCopyUrl = () => {
    if (!calendarToken) return;
    const url = `${window.location.origin}/api/periodics/calendar.ics?token=${calendarToken}`;
    navigator.clipboard.writeText(url).then(() => {
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    });
  };

  if (loading) return <LoadingSpinner message="Carregando contas periódicas..." />;

  const calendarUrl = calendarToken
    ? `${window.location.origin}/api/periodics/calendar.ics?token=${calendarToken}`
    : '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Contas Periódicas</h1>
        {canWrite && (
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" /> Nova Periódica
          </button>
        )}
      </div>

      {/* Calendar feed */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-700">Feed iCalendar (.ics)</h2>
            <p className="text-xs text-gray-500 mt-1">
              Assine essa URL no Google Calendar / Apple Calendar para ver vencimentos no seu calendário.
            </p>
          </div>
        </div>
        {calendarToken ? (
          <div className="flex items-center gap-2 mt-3">
            <input
              type="text"
              readOnly
              value={calendarUrl}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-xs font-mono bg-gray-50"
              onClick={(e) => (e.target as HTMLInputElement).select()}
            />
            <button
              onClick={handleCopyUrl}
              className="inline-flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm"
            >
              <Copy className="h-4 w-4" />
              {tokenCopied ? 'Copiado!' : 'Copiar'}
            </button>
            <button
              onClick={handleRegenerateToken}
              className="inline-flex items-center gap-1 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded text-sm"
              title="Regenerar token (invalida o anterior)"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={handleRegenerateToken}
            className="mt-2 inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
          >
            <Calendar className="h-4 w-4" /> Gerar URL do calendário
          </button>
        )}
      </div>

      {/* Próximos vencimentos */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Próximos vencimentos (1 mês atrás → 3 meses à frente)</h2>
        </div>
        {occurrences.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">Sem ocorrências no período</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Vencimento</th>
                <th className="px-4 py-2 text-left">Descrição</th>
                <th className="px-4 py-2 text-right">Valor esperado</th>
                <th className="px-4 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {occurrences.map((occ, i) => (
                <tr key={`${occ.periodic_id}-${occ.due_date}`} className={i % 2 ? 'bg-gray-50' : ''}>
                  <td className="px-4 py-2">{formatDate(occ.due_date)}</td>
                  <td className="px-4 py-2">{occ.description}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {occ.expected_amount ? formatBRL(parseFloat(occ.expected_amount)) : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={occ.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cadastros */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Cadastros</h2>
        </div>
        {periodics.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500">Nenhuma periódica cadastrada</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">ID</th>
                <th className="px-4 py-2 text-left">Descrição</th>
                <th className="px-4 py-2 text-left">Cadência</th>
                <th className="px-4 py-2 text-left">Conta</th>
                <th className="px-4 py-2 text-right">Valor</th>
                <th className="px-4 py-2 text-center">Ativa</th>
                <th className="px-4 py-2 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {periodics.map((p) => (
                <tr key={p.id} className="border-t border-gray-100">
                  <td className="px-4 py-2 font-mono text-xs">{p.id}</td>
                  <td className="px-4 py-2">{p.description}</td>
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {CADENCE_LABELS[p.cadence]} (dia {p.day_of_month}
                    {p.cadence === 'yearly' && p.month ? `/${p.month}` : ''}
                    {p.cadence === 'every_n_months' && p.n_months ? `, a cada ${p.n_months}m` : ''}
                    )
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-700">{p.account}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {p.expected_amount ? formatBRL(parseFloat(p.expected_amount)) : '—'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={p.active}
                      onChange={() => canWrite && handleToggleActive(p)}
                      disabled={!canWrite}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    {canWrite && (
                      <>
                        <button
                          onClick={() => openEdit(p)}
                          className="text-blue-600 hover:underline text-xs mr-3"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          Remover
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">{editing ? 'Editar' : 'Nova'} Periódica</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">ID (slug)</span>
                  <input
                    type="text"
                    required
                    disabled={!!editing}
                    value={form.id}
                    onChange={(e) => setForm({ ...form, id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm disabled:bg-gray-100"
                    placeholder="internet-vivo"
                    pattern="[a-z0-9][a-z0-9_\-]*"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Descrição</span>
                  <input
                    type="text"
                    required
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Conta de despesa</span>
                  <input
                    type="text"
                    required
                    list="accounts-datalist"
                    value={form.account}
                    onChange={(e) => setForm({ ...form, account: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Conta de pagamento (banco)</span>
                  <input
                    type="text"
                    required
                    list="accounts-datalist"
                    value={form.payment_account}
                    onChange={(e) => setForm({ ...form, payment_account: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Ativos:Banco:..."
                  />
                </label>
                <datalist id="accounts-datalist">
                  {accounts.map((a) => (
                    <option key={a} value={a} />
                  ))}
                </datalist>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Cadência</span>
                  <select
                    value={form.cadence}
                    onChange={(e) => setForm({ ...form, cadence: e.target.value as PeriodicCadence })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="monthly">Mensal</option>
                    <option value="yearly">Anual</option>
                    <option value="every_n_months">A cada N meses</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Dia do mês</span>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.day_of_month}
                    onChange={(e) => setForm({ ...form, day_of_month: parseInt(e.target.value) || 1 })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </label>
                {form.cadence === 'yearly' && (
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">Mês (1-12)</span>
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={form.month ?? ''}
                      onChange={(e) =>
                        setForm({ ...form, month: e.target.value ? parseInt(e.target.value) : null })
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </label>
                )}
                {form.cadence === 'every_n_months' && (
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700">A cada N meses</span>
                    <input
                      type="number"
                      min={1}
                      value={form.n_months ?? ''}
                      onChange={(e) =>
                        setForm({ ...form, n_months: e.target.value ? parseInt(e.target.value) : null })
                      }
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Valor esperado (R$)</span>
                  <input
                    type="text"
                    value={form.expected_amount ?? ''}
                    onChange={(e) => setForm({ ...form, expected_amount: e.target.value || null })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="299.90"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Alerta (dias antes)</span>
                  <input
                    type="number"
                    min={0}
                    value={form.alert_days_before}
                    onChange={(e) => setForm({ ...form, alert_days_before: parseInt(e.target.value) || 0 })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </label>
                <label className="flex items-center mt-5">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm">Ativa</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Início</span>
                  <input
                    type="date"
                    required
                    value={form.start}
                    onChange={(e) => setForm({ ...form, start: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-700">Fim (opcional)</span>
                  <input
                    type="date"
                    value={form.end ?? ''}
                    onChange={(e) => setForm({ ...form, end: e.target.value || null })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </label>
              </div>

              {formError && <div className="text-sm text-red-600">{formError}</div>}

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
